import { useEffect, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import './ScratchCard.css';
import scratchSurface from '../assets/visuel-officiel-nature-power.png';
import { useLanguage } from '../contexts/LanguageContext';

const IMAGE_BASE_URL = process.env.REACT_APP_ASSET_BASE_URL || window.location.origin;

const PRIZE_IMAGE_MAP = {
  bouteille_nature_power: 'BOUTEILLE NATURE POWER.jpg.jpeg',
  bouteille: 'BOUTEILLE NATURE POWER.jpg.jpeg',
  casquette: 'CASQUETTE LARGE BORD NATURE POWER.png',
  casquette_large_bord: 'CASQUETTE LARGE BORD NATURE POWER.png',
  polo: 'polo nature power.jpeg',
  polo_nature_power: 'polo nature power.jpeg',
  bloc_note: 'SIMULATION BLOC NOTE NATURE POWER.png',
  bloc_notes: 'SIMULATION BLOC NOTE NATURE POWER.png',
};

const LABEL_IMAGE_MAP = {
  'bouteille nature power': 'BOUTEILLE NATURE POWER.jpg.jpeg',
  'bouteille reaktor nature power': 'BOUTEILLE NATURE POWER.jpg.jpeg',
  'bouteille': 'BOUTEILLE NATURE POWER.jpg.jpeg',
  'casquette large bord nature power': 'CASQUETTE LARGE BORD NATURE POWER.png',
  'casquette large bord': 'CASQUETTE LARGE BORD NATURE POWER.png',
  'casquette': 'CASQUETTE LARGE BORD NATURE POWER.png',
  'polo nature power': 'polo nature power.jpeg',
  'polo': 'polo nature power.jpeg',
  'bloc note nature power': 'SIMULATION BLOC NOTE NATURE POWER.png',
  'bloc notes nature power': 'SIMULATION BLOC NOTE NATURE POWER.png',
  'bloc note': 'SIMULATION BLOC NOTE NATURE POWER.png',
  'bloc notes': 'SIMULATION BLOC NOTE NATURE POWER.png',
};

const normalizeKey = (value) => {
  if (!value) return '';
  return value.toString().toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ').trim();
};

const normalizeAssetUrl = (value) => {
  if (!value) return null;
  const trimmed = value.toString().trim();
  if (!trimmed) return null;
  if (/^(https?:)?\/\//i.test(trimmed) || trimmed.startsWith('/') || trimmed.startsWith('data:')) return trimmed;
  return `${IMAGE_BASE_URL}/${encodeURIComponent(trimmed)}`;
};

const resolveImageUrl = (prize) => {
  if (!prize) return null;
  const typeKey = normalizeKey(prize.prize_type).replace(/ /g, '_');
  const direct = PRIZE_IMAGE_MAP[prize.prize_type] || PRIZE_IMAGE_MAP[typeKey];
  const labelKey = normalizeKey(prize.prize_label);
  const nameKey = normalizeKey(prize.name);
  const filename = direct || LABEL_IMAGE_MAP[labelKey] || LABEL_IMAGE_MAP[nameKey];
  if (!filename) return null;
  return `${IMAGE_BASE_URL}/${encodeURIComponent(filename)}`;
};

const isLosingPrize = (prize) => {
  if (!prize?.prize_type) return false;
  const t = prize.prize_type.toLowerCase();
  return t.includes('try_again') || t.includes('reessayer') || t.includes('perdu');
};

const CARD_W = 320;
const CARD_H = Math.round(320 * 483 / 919);

const ScratchCard = ({ prize, onReveal, isRevealed }) => {
  const { t, tPrize } = useLanguage();
  const canvasRef = useRef(null);
  const [isScratching, setIsScratching] = useState(false);
  const [scratchPercentage, setScratchPercentage] = useState(0);
  const [autoRevealed, setAutoRevealed] = useState(false);
  const [canvasReady, setCanvasReady] = useState(false);

  const imageUrl = resolveImageUrl(prize);
  const iconUrl = normalizeAssetUrl(prize?.icon);
  const isLosing = isLosingPrize(prize);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const img = new Image();
    img.onload = () => {
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      const scale = Math.max(CARD_W / iw, CARD_H / ih);
      const dw = iw * scale;
      const dh = ih * scale;
      const dx = (CARD_W - dw) / 2;
      const dy = (CARD_H - dh) / 2;
      ctx.drawImage(img, dx, dy, dw, dh);
      setCanvasReady(true);
    };
    img.src = scratchSurface;
  }, []);

  const calculateScratchPercentage = (ctx, w, h) => {
    const data = ctx.getImageData(0, 0, w, h).data;
    let transparent = 0;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 128) transparent++;
    }
    return (transparent / (data.length / 4)) * 100;
  };

  const getPos = (canvas, e) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const scratch = (x, y) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 28, 0, Math.PI * 2);
    ctx.fill();

    const pct = calculateScratchPercentage(ctx, canvas.width, canvas.height);
    setScratchPercentage(pct);

    if (pct > 50 && !autoRevealed) {
      setAutoRevealed(true);
      const ctx2 = canvas.getContext('2d');
      ctx2.clearRect(0, 0, canvas.width, canvas.height);
      setScratchPercentage(100);
      if (!isLosing) {
        setTimeout(() => {
          confetti({
            particleCount: 180,
            spread: 90,
            origin: { y: 0.55 },
            colors: ['#00A651', '#ffffff', '#FFD700', '#006633', '#a8e6c0'],
            startVelocity: 45,
            gravity: 0.8,
            scalar: 1.1,
          });
        }, 300);
      }
      if (onReveal && !isRevealed) setTimeout(() => onReveal(), 300);
    }
  };

  const handleStart = (e) => {
    if (isRevealed) return;
    e.preventDefault();
    setIsScratching(true);
    const pos = getPos(canvasRef.current, e);
    scratch(pos.x, pos.y);
  };

  const handleMove = (e) => {
    if (!isScratching || isRevealed) return;
    e.preventDefault();
    const pos = getPos(canvasRef.current, e);
    scratch(pos.x, pos.y);
  };

  const handleEnd = () => setIsScratching(false);

  return (
    <div className="scratch-card-container">
      <div className="scratch-card">
        <div className={`scratch-card-content ${isLosing ? 'losing' : 'winning'}`}>
          {isLosing ? (
            <div className="lose-content">
              <div className="lose-icon">😢</div>
              <p className="lose-text">{t('loseTitle')}</p>
              <p className="lose-sub">{t('loseSub')}</p>
            </div>
          ) : (
            prize && (
              <>
                <div className="prize-img-wrap">
                  {imageUrl ? (
                    <img src={imageUrl} alt={tPrize(prize.prize_label) || 'Lot'} className="prize-img" />
                  ) : prize.image_url ? (
                    <img src={prize.image_url} alt={tPrize(prize.prize_label) || 'Lot'} className="prize-img" />
                  ) : iconUrl ? (
                    <img src={iconUrl} alt={tPrize(prize.prize_label) || 'Lot'} className="prize-img" />
                  ) : (
                    <span style={{ fontSize: 48 }}>🎁</span>
                  )}
                </div>
                <div className="prize-details">
                  <p className="prize-name-text">{tPrize(prize.prize_label || prize.name)}</p>
                  {prize.prize_code && (
                    <div className="prize-code-box">CODE : {prize.prize_code}</div>
                  )}
                </div>
              </>
            )
          )}
        </div>

        {!isRevealed && (
          <>
            {!canvasReady && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e8f5e9', borderRadius: '12px' }}>
                <div style={{ width: 36, height: 36, border: '4px solid #00C060', borderTop: '4px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></div>
              </div>
            )}
            <canvas
              ref={canvasRef}
              width={CARD_W}
              height={CARD_H}
              className={`scratch-surface${isScratching ? ' scratching' : ''}`}
              style={{ opacity: canvasReady ? 1 : 0, transition: 'opacity 0.2s' }}
              onMouseDown={handleStart}
              onMouseMove={handleMove}
              onMouseUp={handleEnd}
              onMouseLeave={handleEnd}
              onTouchStart={handleStart}
              onTouchMove={handleMove}
              onTouchEnd={handleEnd}
            />
          </>
        )}
      </div>

      {!isRevealed && scratchPercentage > 0 && scratchPercentage < 50 && (
        <div className="scratch-hint">{t('keepScratching')}</div>
      )}
    </div>
  );
};

export default ScratchCard;
